const CUSTOMS = 'ADUANA'

const transformInitialDataResponse = initialDataResponse => {
  const parseFixedPickups = center => {
    const parseFixedPickup = pickups =>
      pickups?.map(pickup => ({
        name: pickup.name,
        fromHour: pickup.from_hour,
        toHour: pickup.to_hour,
      })) || []

    if (center?.fixed_pickups?.has_fixed_pickup && center?.client_status_type_code === 'ACTIVE') {
      const groupedPickups = center.fixed_pickups.pickups.reduce(
        (acc, pickup) => {
          if (!pickup.address || (pickup?.status && pickup?.status !== 'Active')) {
            return acc;
          }

          acc[pickup.address] = {
            address: pickup.address,
            city: pickup.address_2,
            postalCode: pickup.postal_code,
            country: pickup.country_code,
            timeZones: [
              acc[pickup.address]?.timeZones?.[0]?.length
                ? acc[pickup.address]?.timeZones?.[0]
                : parseFixedPickup(pickup.pickups_sunday),
              acc[pickup.address]?.timeZones?.[1]?.length
                ? acc[pickup.address]?.timeZones?.[1]
                : parseFixedPickup(pickup.pickups_monday),
              acc[pickup.address]?.timeZones?.[2]?.length
                ? acc[pickup.address]?.timeZones?.[2]
                : parseFixedPickup(pickup.pickups_tuesday),
              acc[pickup.address]?.timeZones?.[3]?.length
                ? acc[pickup.address]?.timeZones?.[3]
                : parseFixedPickup(pickup.pickups_wednesday),
              acc[pickup.address]?.timeZones?.[4]?.length
                ? acc[pickup.address]?.timeZones?.[4]
                : parseFixedPickup(pickup.pickups_thursday),
              acc[pickup.address]?.timeZones?.[5]?.length
                ? acc[pickup.address]?.timeZones?.[5]
                : parseFixedPickup(pickup.pickups_friday),
              acc[pickup.address]?.timeZones?.[6]?.length
                ? acc[pickup.address]?.timeZones?.[6]
                : parseFixedPickup(pickup.pickups_saturday),
            ],
          };

          return acc;
        },
        {}
      );
      return Object.values(groupedPickups)
    } else {
      return null
    }
  }

  const userData = initialDataResponse.data[0]

  const centres = userData.client_centers.map(clientCenter => ({
    id: clientCenter.client_center_code,
    hideId: clientCenter.client_center_id,
    name:
      clientCenter.client_center_name ??
      clientCenter.client_center_business_name,
    shippingAddress: {
      name: clientCenter.client_center_name,
      address: clientCenter.client_center_address,
      country: clientCenter.client_center_country_code,
      postalCode: clientCenter.client_center_postal_code,
      city: clientCenter.client_center_address_2,
      email: clientCenter.client_center_contact_email,
      phone: clientCenter.client_center_contact_phone,
    },
    serviceTypes: clientCenter.shipping_types.map(serviceType => ({
      name: serviceType.shipping_type_name,
      code: serviceType.shipping_type_code,
    })),
    fixedPickupsAdresses: parseFixedPickups(clientCenter),
    status: clientCenter.client_status_type_code,
    compromisedShipments: clientCenter?.compromised_shipments_value,
  }))

  const sortedCentres = centres.sort((centreA, centreB) => {
    if (centreA.id > centreB.id) return 1
    if (centreA.id < centreB.id) return -1

    return 0
  })

  const defaultCentreCode =
    initialDataResponse.default_centreCode ?? sortedCentres[0].id

  // NOTE Quita el prefijo 'WEB' para ordenar por número, y vuelve a ponerlo después.
  // REVIEW Puede romperse si el estado no tiene prefijo.
  const orderedStatusCodes = userData.tracking_status
    .map(status => parseInt(status.status_code.slice(3), 10))
    .sort((a, b) => {
      if (a === 2300 && b === 2100) {
        return -1
      }
      if (a === 2100 && b === 2300) {
        return 1
      }
      return a - b
    })
    .map(number => `WEB${number.toString().padStart(4, '0')}`)

  const statusList = orderedStatusCodes.map(statusCode => {
    const status = userData.tracking_status.find(
      s => s.status_code === statusCode,
    )

    return {
      code: status.status_code,
      name: status.client_status_name,
      canPrint: status.allow_print,
      canCancel: status.allow_cancel,
    }
  })

  return {
    data: {
      clientCode: userData.client_code,
      clientId: userData.client_id,
      defaultCentreCode,
      centres: sortedCentres,
      statusList,
      webUserCode: userData.web_user_code,
      webUserEmail: userData.web_user_email,
      webUserName: userData.web_user_name,
      webUserPhone: userData.web_user_phone,
      isFinancial: userData.is_financial,
      isMaster: userData.is_client_master,
      // BK/FR identity fields
      businessType: userData.business_type ?? null,
      webUserRole: userData.web_user_role ?? null,
      clientName: userData.client_name ?? null,
      fiscalCode: userData.fiscal_code ?? null,
      isBrokerOrFranchise: ['BROKER', 'FRANCHISE'].includes(userData.business_type),
      // Effective permissions (flattened from backend, null-safe for legacy users)
      canManageClients: userData.effective_permissions?.can_manage_clients ?? false,
      canManageUsers: userData.effective_permissions?.can_manage_users ?? false,
      canDeleteColaborador: userData.effective_permissions?.can_delete_colaborador ?? false,
      canDeleteGestor: userData.effective_permissions?.can_delete_gestor ?? false,
      canViewAllCenters: userData.effective_permissions?.can_view_all_centers ?? false,
      canResetPasswords: userData.effective_permissions?.can_reset_passwords ?? false,
      canCreateAdminUsers: userData.effective_permissions?.can_create_admin_users ?? false,
    },
  }
}

const transformServiceTypesResponse = data => {
  const serviceTypes = data.data[0].shipping_type_codes.map(serviceType => ({
    originalCode: serviceType.shipping_type_code,
    code: `${serviceType.shipping_type_code}-${serviceType.shipping_type_display_name}`,
    label: serviceType.shipping_type_display_name,
    description: serviceType.shipping_type_description,
    isSingleItem: serviceType.is_single_item === 1,
    additionals: serviceType.additionals,
    basePrice: serviceType.subject_tax_amount
      ? serviceType.subject_tax_amount.toFixed(2)
      : null,
    transportPrice: serviceType.transport_amount
      ? serviceType.transport_amount.toFixed(2)
      : null,
    additionalsPrice: serviceType.additionals_amount
      ? serviceType.additionals_amount.toFixed(2)
      : null,
    fuelAmount: serviceType.fuel_amount.toFixed(2),
    maxItemWeight: serviceType.max_item_weight,
    maxPerimeter: serviceType.max_perimeter,
    maxSideDimension: serviceType.max_side_dimension,
    items: serviceType?.items,
    totalVolumetricWeight: serviceType?.total_volumetric_weight
  }))

  return {
    serviceTypes,
    isEuropeanUnion: !!data.data[0].is_european_union,
    hasCustom: !!data.data[0].has_custom,
    allowsElectronicalTradeDocuments:
      !!data.data[0].allows_electronical_trade_documents,
  }
}

const transformTransitRulesResponse = transitRulesData => {
  const data = transitRulesData.data[0]
  const documents = data.documents.map(document => ({
    name: document.document_name,
    path: document.document_path,
    type: document.document_type,
  }))

  return {
    documents,
    shippingType: data.transit_rule_type_code,
    hasCustoms: data.transit_rule_type_code === CUSTOMS,
  }
}

export {
  transformServiceTypesResponse,
  transformInitialDataResponse,
  transformTransitRulesResponse,
}
